export type BackgroundRemovalService = (
  input: Blob,
  signal: AbortSignal,
  progress?: (message: string) => void,
) => Promise<Blob>;

/** Cutaway's local AI engine, isolated in a cancellable worker. No image leaves it. */
export class BackgroundRemover {
  constructor(private baseUrl = "/background-removal/v1/") {}

  remove: BackgroundRemovalService = async (input, signal, progress) => {
    signal.throwIfAborted();
    if (
      !input.size ||
      input.size > 20 * 1024 * 1024 ||
      !["image/png", "image/jpeg", "image/webp"].includes(input.type)
    )
      throw new Error("Choose a PNG, JPEG or WebP image smaller than 20 MB.");
    const base = new URL(this.baseUrl, location.href);
    if (
      !["http:", "https:"].includes(base.protocol) ||
      base.username ||
      base.password
    )
      throw new Error("Invalid background remover location.");
    const workerUrl = new URL("worker.js", base).href;
    // A blob bootstrap lets an embedded host import public code from its proof server.
    const bootstrap = URL.createObjectURL(
      new Blob([`import ${JSON.stringify(workerUrl)};`], {
        type: "text/javascript",
      }),
    );
    let worker: Worker;
    try {
      worker = new Worker(bootstrap, { type: "module", name: "Cutaway" });
    } catch (error) {
      URL.revokeObjectURL(bootstrap);
      throw error;
    }
    try {
      return await new Promise<Blob>((resolve, reject) => {
        const abort = () => reject(new DOMException("Cancelled", "AbortError"));
        signal.addEventListener("abort", abort, { once: true });
        const done = (callback: () => void) => {
          signal.removeEventListener("abort", abort);
          callback();
        };
        worker.onerror = (event) => {
          event.preventDefault();
          done(() =>
            reject(
              new Error("Background removal could not start. Please retry."),
            ),
          );
        };
        worker.onmessage = (event) => {
          if (signal.aborted) return;
          if (event.data.type === "progress") progress?.(event.data.message);
          else if (
            event.data.type === "result" &&
            event.data.blob instanceof Blob
          )
            done(() => resolve(event.data.blob));
          else if (event.data.type === "error")
            done(() => reject(new Error(event.data.message)));
        };
        if (signal.aborted) {
          abort();
          return;
        }
        worker.postMessage({ input, publicPath: new URL("data/", base).href });
      });
    } finally {
      worker.terminate();
      URL.revokeObjectURL(bootstrap);
    }
  };
}

export const createBackgroundRemover = (
  baseUrl?: string,
): BackgroundRemovalService => new BackgroundRemover(baseUrl).remove;
export const defaultBackgroundRemover = createBackgroundRemover();
